import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { routes } from '../../../../environments/shared-environment/routes';
import { AuthService } from '../../services/auth/auth.service';
import { RefreshTokenResult } from '../../services/auth/refresh-token-result';
import { MeService } from '../../services/me/me.service';
import { SessionStateService } from '../../services/session/session-state.service';

export const AUTH_RETRY = new HttpContextToken<boolean>(() => false);

let refreshInProgress: Promise<RefreshTokenResult> | null = null;

function isAuthRoute(url: string): boolean {
  return [routes.auth.login, routes.auth.refresh, routes.auth.logout].some(route => url.includes(route));
}

function isMeRoute(url: string): boolean {
  return url.includes(routes.users.me);
}

function isLegalDocumentsRoute(url: string): boolean {
  return url.includes(routes.legal.documents);
}

function getRefreshPromise(authService: AuthService): Promise<RefreshTokenResult> {
  if (!refreshInProgress) {
    refreshInProgress = authService.refreshToken().finally(() => {
      refreshInProgress = null;
    });
  }

  return refreshInProgress;
}

function redirectToLogin(meService: MeService, router: Router, sessionState: SessionStateService): void {
  sessionState.markRevoked();
  meService.me = null;
  void router.navigateByUrl(environment.configs.unauthenticatedRoute, { replaceUrl: true });
}

export const refreshToken: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  if (isAuthRoute(req.url)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const meService = inject(MeService);
  const router = inject(Router);
  const sessionState = inject(SessionStateService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (req.context.get(AUTH_RETRY)) {
        redirectToLogin(meService, router, sessionState);
        return throwError(() => error);
      }

      if (isMeRoute(req.url)) {
        meService.me = null;
        return throwError(() => error);
      }

      if (isLegalDocumentsRoute(req.url)) {
        return throwError(() => error);
      }

      return from(getRefreshPromise(authService)).pipe(
        switchMap(refreshResult => {
          if (refreshResult === 'networkError') {
            return throwError(() => error);
          }

          if (refreshResult === 'authFailed') {
            redirectToLogin(meService, router, sessionState);
            return throwError(() => error);
          }

          const retryReq = req.clone({
            context: req.context.set(AUTH_RETRY, true),
            withCredentials: true
          });

          return next(retryReq);
        })
      );
    })
  );
};
